import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { api, errorMessage, Field, inputClass, ringStyle } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";

const OPTIONS = [
  {
    value: "take_down_all",
    title: "Take down my catalogue",
    text: "Every live track is taken down when the account is deactivated: videos using them are muted and buyers are refunded or their licences ended, as with a normal take-down.",
  },
  {
    value: "keep_existing_licences",
    title: "Keep existing licences active",
    text: "Licences that have already been sold stay valid until they expire and buyers are told. No new Creator or Business licences are offered.",
  },
];

function DeactivateForm({ onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const {
    register, handleSubmit, watch,
    formState: { errors },
  } = useForm({ defaultValues: { catalogue_disposition: "", reason: "", confirm: false } });
  const selected = watch("catalogue_disposition");

  const onSubmit = async (data) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await api.post("deactivate", {
        catalogue_disposition: data.catalogue_disposition,
        reason: data.reason.trim() || undefined,
        confirm: true,
      });
      if (response.data?.success) {
        toast.success("Your artist account has been deactivated.");
        onDone(response.data.result);
      }
    } catch (error) {
      toast.error(errorMessage(error, "Could not deactivate your account. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-6">
      <p className="text-gray-500">
        Deactivating hides your artist profile and stops new activity. Choose what should happen to your catalogue.
        The catalogue team applies your choice - tracks are not removed instantly.
      </p>

      <fieldset>
        <legend className="block text-sm font-bold text-gray-900 mb-2">What should happen to your catalogue?</legend>
        <div className="space-y-3">
          {OPTIONS.map((o) => (
            <label
              key={o.value}
              className={`flex gap-3 rounded-xl border p-4 cursor-pointer bg-white ${
                selected === o.value ? "border-gray-900" : "border-gray-200"
              }`}
            >
              <input
                type="radio"
                value={o.value}
                className="mt-1"
                {...register("catalogue_disposition", { required: "Choose what should happen to your catalogue." })}
              />
              <span>
                <span className="block font-semibold text-gray-900">{o.title}</span>
                <span className="block text-sm text-gray-500">{o.text}</span>
              </span>
            </label>
          ))}
        </div>
        {errors.catalogue_disposition && <p className="text-red-600 text-sm mt-1">{errors.catalogue_disposition.message}</p>}
      </fieldset>

      <Field label="Reason (optional)" error={errors.reason?.message}>
        <textarea
          rows={3}
          className={inputClass(errors.reason)}
          style={ringStyle}
          {...register("reason", { maxLength: { value: 500, message: "Reason must be at most 500 characters." } })}
        />
      </Field>

      <div>
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input type="checkbox" className="mt-1" {...register("confirm", { required: "Please confirm you want to deactivate your artist account." })} />
          I understand my artist account will be deactivated.
        </label>
        {errors.confirm && <p className="text-red-600 text-sm mt-1">{errors.confirm.message}</p>}
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full font-semibold text-white px-8 py-3 bg-red-600 hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {submitting ? "Deactivating..." : "Deactivate account"}
        </button>
      </div>
    </form>
  );
}

export default function DeactivateAccount() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} title="Deactivate artist account">
      <DeactivateForm onDone={account.setApplication} />
    </StudioGate>
  );
}
